import { useToast } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubPackageVisibility } from "@enums";
import type { IHubPackage } from "@models";
import { useHubCatalogsByIds, useUpdateHubPackage } from "@query";
import { isCatalogVisibilityAllowedInPackage } from "@utils";
import { useCallback, useMemo } from "react";

export function useEditPackageSave(
  cartPackage: IHubPackage | undefined | null,
  setEditPackageOpen: (open: boolean) => void,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutate: updateHubPackage, isPending: isUpdatePending } =
    useUpdateHubPackage();

  const catalogIdsFromPackage = useMemo(() => {
    return (
      cartPackage?.catalogRelations
        ?.map((r) => ({
          catalogId: r.catalogId,
          version: r.catalogVersion,
        }))
        .filter(
          (item): item is { catalogId: number; version: number } =>
            item.catalogId != null &&
            item.catalogId > 0 &&
            item.version != null,
        ) ?? []
    );
  }, [cartPackage?.catalogRelations]);

  const { data: allCatalogs } = useHubCatalogsByIds(catalogIdsFromPackage, {
    enabled: catalogIdsFromPackage.length > 0,
  });

  const handleEditPackageSave = useCallback(
    (values: {
      label: string;
      description: string;
      visibility: IHubPackageVisibility;
    }) => {
      const source = cartPackage;
      if (source != null) {
        if (source.packageId == null || source.packageId <= 0) return;

        if (allCatalogs && allCatalogs.length > 0) {
          const hasMismatch = allCatalogs.some(
            (c) =>
              !isCatalogVisibilityAllowedInPackage(
                c.visibility,
                values.visibility,
              ),
          );
          if (hasMismatch) {
            toast.add({
              title: t("hub.catalog_manager.package_visibility_mismatch", {
                defaultValue:
                  "Catalog visibility must match package visibility.",
              }),
              data: { type: "error" },
            });
            return;
          }
        }

        const body: IHubPackage = {
          ...source,
          name: values.label,
          label: values.label,
          description: values.description,
          visibility: values.visibility,
          catalogRelations: source.catalogRelations ?? [],
        };
        updateHubPackage(body, {
          onSuccess: () => {
            setEditPackageOpen(false);
            toast.add({
              title: t("hub.package_manager.edit_success", {
                defaultValue: "Package updated successfully",
              }),
              data: { type: "success" },
            });
          },
          onError: (error: unknown) => {
            const apiError = error as { status?: number; message?: string };
            toast.add({
              title: t("hub.package_manager.edit_error", {
                defaultValue: "Could not update package",
              }),
              ...(apiError.status === 409 && apiError.message
                ? { description: apiError.message }
                : {}),
              data: { type: "error" },
            });
          },
        });
      }
    },
    [cartPackage, allCatalogs, updateHubPackage, t, toast, setEditPackageOpen],
  );

  return { handleEditPackageSave, isEditPackagePending: isUpdatePending };
}
